import { paths } from '@common/types';
import { Box, Button, Chip, Divider, Typography } from '@mui/material';
import { t } from 'i18next';
import LogoutIcon from '@mui/icons-material/Logout';

import { ProfileAvatar } from 'components/ProfileAvatar';
import { ConfirmationModal } from 'components/ConfirmationModal';

import { useDetailsCard } from './hooks';
import { UploadAvatarModal } from './UploadAvatarModal';
import { AssignmentListItem } from './AssignmentListItem';
import { TeamAssignmentListItem } from './TeamAssignmentListItem';
import React from 'react';

const AssignmentDivider = () => (
  <Divider
    orientation='vertical'
    sx={{
      width: '0.5rem',
      borderRightWidth: '2px',
      marginTop: '0.5rem',
      marginBottom: '1rem',
      height: '1.5rem',
      pl: '2.5rem',
    }}
  />
);

interface IDetailsCardProps {
  profile: paths[`/profile/{user_id}`]['get']['responses']['200']['content']['application/json'] & {
    id: string;
  };
}

export const DetailsCard = ({ profile }: IDetailsCardProps) => {
  const {
    isDeleteConfirmationOpen,
    handleDeleteConfirmationOpen,
    handleDeleteConfirmationClose,
    confirmDelete,
    isSignoutConfirmationOpen,
    handleSignoutConfirmationOpen,
    handleSignoutConfirmationClose,
    confirmSignout,
    isUploadOpen,
    closeUpload,
    openUpload,
  } = useDetailsCard();

  return (
    <Box
      sx={{
        width: 'fit-content',
        padding: '1.5rem',
        boxShadow: 'initial',
        backgroundColor: 'white',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100% - 4rem)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <ProfileAvatar
          encoded_image={profile.image?.thumbnail_xsmall}
          size='large'
          firstName={profile.first_name}
          lastName={profile.last_name}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', pl: '1rem' }}>
          <Typography variant='h6'>
            {profile.first_name} {profile.last_name}
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: theme => theme.palette.semiBlack.main }}
          >
            {profile.email}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          my: '1rem',
          width: '100%',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        {profile.assignments &&
          profile.assignments.map((assignment, index) => (
            <Chip
              key={`${assignment.record.id} + ${index}`}
              sx={{
                backgroundColor: 'rgba(255, 110, 64, 1)',
                color: 'rgba(255, 255, 255, 1)',
              }}
              label={assignment.role}
            />
          ))}
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          gap: '1rem',
        }}
      >
        <Button
          type='button'
          variant='outlined'
          color='primary'
          sx={{
            fontSize: '0,875rem',
            fontWeight: 'medium',
            lineHeight: '24px',
            color: 'primary.main',
            width: '100%',
          }}
          onClick={openUpload}
          loadingPosition='center'
        >
          {t('profile.detailsCard.editPictureLabel')}
        </Button>

        <Button
          type='button'
          variant='outlined'
          color='primary'
          sx={{
            fontSize: '0,875rem',
            fontWeight: 'medium',
            lineHeight: '24px',
            color: 'primary.main',
            width: '100%',
          }}
          onClick={handleDeleteConfirmationOpen}
          loadingPosition='center'
          disabled={!profile.image?.thumbnail_xsmall}
        >
          {t('profile.detailsCard.deletePictureLabel')}
        </Button>
      </Box>

      <Typography variant='subtitle2' sx={{ mt: '2rem', mb: '0.5rem' }}>
        {t('profile.detailsCard.assignmentsTitle')}
      </Typography>

      <Box sx={{ width: '100%', flexGrow: 1, overflowY: 'scroll' }}>
        {profile.assignments?.length &&
          profile.assignments.map((entry, index) => {
            return (
              <React.Fragment key={entry.record.id}>
                <AssignmentListItem key={entry.record.id} assignment={entry} />
                {index !== profile.assignments.length - 1 && (
                  <AssignmentDivider />
                )}
              </React.Fragment>
            );
          })}
        {profile.team && (
          <>
            {profile.assignments?.length && <AssignmentDivider />}
            <TeamAssignmentListItem team={profile.team} />
          </>
        )}
      </Box>

      <Button
        type='button'
        variant='outlined'
        color='error'
        sx={{
          fontSize: '0,875rem',
          fontWeight: 'medium',
          lineHeight: '24px',
          color: 'error',
          width: '100%',
          mt: '2rem',
        }}
        loadingPosition='center'
        onClick={handleSignoutConfirmationOpen}
        startIcon={<LogoutIcon />}
      >
        {t('profile.detailsCard.signOut')}
      </Button>

      <ConfirmationModal
        title={t('profile.detailsCard.deletePictureConfirmationTitle')}
        message={t('profile.detailsCard.deletePictureConfirmationDescription')}
        onConfirm={confirmDelete}
        onClose={handleDeleteConfirmationClose}
        isOpen={isDeleteConfirmationOpen}
      />

      <ConfirmationModal
        title={t('profile.detailsCard.signoutConfirmationTitle')}
        message={t('profile.detailsCard.signoutConfirmationDescription')}
        onConfirm={confirmSignout}
        onClose={handleSignoutConfirmationClose}
        isOpen={isSignoutConfirmationOpen}
        confirmText={'SIGN OUT'}
      />

      <UploadAvatarModal onClose={closeUpload} isOpen={isUploadOpen} />

      {/* END:  modals */}
    </Box>
  );
};
